import React, { useState, useEffect, useRef } from "react";
import "./Contact.css";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  MessageSquare,
  Sparkles,
  Globe,
  Zap,
  Headset,
  SendHorizontal
} from "lucide-react";
import SEO from "../components/SEO";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [status, setStatus] = useState("");
  const containerRef = useRef(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('nova-reveal-in-view');
        }
      });
    }, { threshold: 0.1 });

    const hiddenElements = document.querySelectorAll('.nova-reveal');
    hiddenElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleMouseMove = (e, index) => {
    let card;
    if (index === 'form') {
      card = containerRef.current;
    } else {
      card = cardRefs.current[index];
    }

    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = (y - centerY) / 60;
    const rotateY = (centerX - x) / 60;

    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1, 1, 1)`;

    const glow = card.querySelector('.nova-glow');
    if (glow) {
      glow.style.background = `radial-gradient(circle at ${x}px ${y}px, rgba(255,102,0,0.15) 0%, transparent 80%)`;
    }
  };

  const handleMouseLeave = (index) => {
    let card;
    if (index === 'form') {
      card = containerRef.current;
    } else {
      card = cardRefs.current[index];
    }

    if (!card) return;
    card.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`;
    const glow = card.querySelector('.nova-glow');
    if (glow) {
      glow.style.background = `transparent`;
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus("Sending...");
    setTimeout(() => {
      setStatus("Message Sent!");
      setFormData({ name: "", email: "", subject: "", message: "" });
      setTimeout(() => setStatus(""), 3000);
    }, 1500);
  };

  const infoItems = [
    {
      icon: <MapPin className="nova-icon-move" />,
      title: "Eluru",
      lines: ["D.No: 16-58/33, Sai Sadan,", "Hanuman Nagar, Eluru,", "AP– 534001, India"],
      color: "#ff6600"
    },
    {
      icon: <MapPin className="nova-icon-move" />,
      title: "Vijayawada ",
      lines: ["D.No: 12-22, First Floor,", "Behind Alankar Bakery,", "Enikepadu, Vijayawada - 521108"],
      color: "#10b981"
    },
    {
      icon: <Phone className="nova-icon-move" />,
      title: "Call Us",
      lines: ["HR: +91 82972 22323", "Biz: +91 93943 22302"],
      color: "#0084ff"
    },
    {
      icon: <Mail className="nova-icon-move" />,
      title: "Email Us",
      lines: ["hr@dbasesolutions.in", "info@dbasesolutions.in"],
      color: "#ff3366"
    },
    {
      icon: <Clock className="nova-icon-move" />,
      title: "Working Hours",
      lines: ["Mon-Sat: 9:30 - 17:30", "Sunday: Closed"],
      color: "#f59e0b"
    }
  ];

  return (
    <div className="nova-contact-wrapper">
      <SEO
        title="Contact Us — D Base Solutions Pvt Ltd"
        description="Get in touch with D Base Solutions Pvt Ltd. Visit our offices in Eluru and Vijayawada, or contact us for ERP demos, exam suite consultation, or support."
        keywords="Contact D Base Solutions, D Base Solutions Eluru address, D Base Solutions Vijayawada office, DBS phone number, DBS email"
        canonical="https://dbasesolutions.in/contact"
        ogImage="/og-contact.png"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Contact", url: "/contact" }
        ]}
      />
      {/* Decorative Background */}
      <div className="nova-bg-ornaments">
        <div className="nova-orb orb-1"></div>
        <div className="nova-orb orb-2"></div>
        <div className="nova-orb orb-3"></div>
        <Sparkles className="nova-float-icon fi-1" />
        <Globe className="nova-float-icon fi-2" />
        <Zap className="nova-float-icon fi-3" />
      </div>

      <div className="nova-container">
        <header className="nova-header nova-reveal">
          <div className="nova-badge">
            <Headset size={16} />
            <span>24/7 Priority Support</span>
          </div>
          <h1 className="nova-title">Get in <span className="nova-accent">Touch</span></h1>
          <p className="nova-subtitle">Have a question or a project in mind? Our team is ready to help you scale your vision.</p>
        </header>

        <div className="nova-grid">
          {/* Left: Interactive Info Cards */}
          <div className="nova-info-section">
            <div className="nova-map-wrapper nova-reveal">
              <iframe
                title="Google Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d6538.514845106263!2d81.08286197457798!3d16.697002303983048!2m3!1f0!2f0!3f0!m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a361500cd99f3e9%3A0x58042bca1eb7b7b1!2sD%20BASE%20SOLUTIONS!5e1!3m2!1sen!2sin!4v1612590819779!5m2!1sen!2sin"
                className="nova-map-frame"
                allowFullScreen=""
                loading="lazy"
              ></iframe>
            </div>

            <div className="nova-info-grid">
              {infoItems.map((item, idx) => (
                <div
                  key={idx}
                  ref={el => cardRefs.current[idx] = el}
                  className="nova-glass-card nova-reveal"
                  onMouseMove={(e) => handleMouseMove(e, idx)}
                  onMouseLeave={() => handleMouseLeave(idx)}
                >
                  <div className="nova-glow"></div>
                  <div className="nova-card-inner">
                    <div className="nova-icon-box" style={{ background: `${item.color}15`, color: item.color }}>
                      {item.icon}
                    </div>
                    <h3>{item.title}</h3>
                    {item.lines.map((line, lIdx) => (
                      <p key={lIdx}>{line}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Premium Form Container */}
          <div className="nova-form-section">
            <div
              ref={containerRef}
              className="nova-form-glass nova-reveal"
              onMouseMove={(e) => handleMouseMove(e, 'form')}
              onMouseLeave={() => handleMouseLeave('form')}
            >
              <div className="nova-glow"></div>
              <div className="nova-form-content">
                <div className="nova-form-head">
                  <MessageSquare className="nova-head-icon" />
                  <h2>Send us a Message</h2>
                </div>

                <form onSubmit={handleSubmit} className="nova-contact-form">
                  <div className="nova-input-row">
                    <div className="nova-field">
                      <label>Your Name</label>
                      <input
                        type="text"
                        name="name"
                        placeholder="e.g. Harish  "
                        required
                        value={formData.name}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="nova-field">
                      <label>Email Address</label>
                      <input
                        type="email"
                        name="email"
                        placeholder="harish@company.com"
                        required
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="nova-field">
                    <label>Subject</label>
                    <input
                      type="text"
                      name="subject"
                      placeholder="How can we help?"
                      required
                      value={formData.subject}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="nova-field">
                    <label>Message</label>
                    <textarea
                      name="message"
                      rows="4"
                      placeholder="Tell us about your project..."
                      required
                      value={formData.message}
                      onChange={handleChange}
                    ></textarea>
                  </div>

                  <button type="submit" className="nova-submit-btn" disabled={status !== ""}>
                    <span>{status ? status : "Launch Message"}</span>
                    {status ? <Sparkles size={20} className="nova-spin" /> : <SendHorizontal size={20} />}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;

